import React, { useState, useEffect } from 'react';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedFooter from '../common/UnifiedFooter';
import EcommerceCartDrawer from '../ecommerce/EcommerceCartDrawer';
import EcommerceAuthModal from '../ecommerce/EcommerceAuthModal';
import EcommerceWhatsAppFloating from '../ecommerce/EcommerceWhatsAppFloating';
import EcommerceCheckoutModal from '../ecommerce/EcommerceCheckoutModal';
import { useWebStore } from '../../context/WebStoreContext';
import { getImageCandidateUrls } from '../../utils/imageHelper';

const EmpresaPage = ({ user, onLogout }) => {
  const { storeConfig, landingConfig: globalLandingConfig, rubros, ecomCustomer: contextCustomer, setEcomCustomer: setContextCustomer, logoutCustomer } = useWebStore();
  const [config, setConfig] = useState(globalLandingConfig || null);

  useEffect(() => {
    if (globalLandingConfig) {
      setConfig(globalLandingConfig);
    }
  }, [globalLandingConfig]);

  // Estados de Carrito y Autenticación
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Mantener ecomCustomer sincronizado con el contexto global
  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalCartCount = cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0);
  const totalCartPrice = cartItems.reduce((acc, item) => acc + ((Number(item.unitPrice) || 0) * (Number(item.quantity) || 1)), 0);
  const activeCustomer = ecomCustomer || (user?.email ? { d_razon: user.username, d_email: user.email } : null);
  const canSeePrices = Boolean(storeConfig?.commercial?.publicPricesEnabled || activeCustomer);

  const about = config?.aboutSection || {
    title: 'Sobre Hiperpel S.A.',
    tagline: 'Más de 30 años de experiencia e innovación en el rubro',
    paragraph1: 'Somos una empresa argentina dedicada a la fabricación y comercialización integral de envases, embalajes, descartables, productos de cartón y papelería comercial.',
    paragraph2: 'Brindamos atención personalizada tanto a comercios minoristas como a industrias y grandes distribuidores de todo el país, garantizando calidad superior, precios competitivos y entregas puntuales.',
    image: '/images/banners/Frente-Web1.jpg',
    stats: [
      { number: '30+', label: 'Años de Trayectoria' },
      { number: '1000+', label: 'Artículos en Catálogo' },
      { number: '24h', label: 'Despacho Rápido' },
      { number: '100%', label: 'Cobertura Nacional' }
    ]
  };

  return (
    <div className="webstore-site-wrapper min-vh-100 d-flex flex-column" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', backgroundColor: 'var(--ecom-bg, #f8fafc)', color: 'var(--ecom-text, #1e293b)' }}>
      
      {/* Cabecera Unificada */}
      <UnifiedHeader
        activePage="empresa"
        cartCount={totalCartCount}
        cartTotal={totalCartPrice}
        currentUser={activeCustomer}
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={() => {
          if (logoutCustomer) {
            logoutCustomer(true);
          } else {
            localStorage.removeItem('sigecom_ecom_customer');
            localStorage.removeItem('sigecom_ecom_last_activity');
            setEcomCustomer(null);
          }
        }}
        onAuthClick={(mode) => {
          setAuthModalMode(mode || 'register');
          setIsAuthModalOpen(true);
        }}
        priceListInfo="Lista Minorista"
        canSeePrices={canSeePrices}
        topBarConfig={config?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
      />

      {/* Banner de Título de Página */}
      <div 
        className="text-white py-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, var(--ecom-primary, #0d6efd) 0%, var(--ecom-secondary, #0a58ca) 100%)'
        }}
      >
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
            <div>
              <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill text-uppercase mb-2">
                Nuestra Empresa
              </span>
              <h1 className="fw-black fs-2 mb-0">{about.title || 'Sobre Nosotros'}</h1>
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="/" className="text-white opacity-75 text-decoration-none">Inicio</a></li>
                <li className="breadcrumb-item active text-white fw-bold" aria-current="page">Empresa</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Contenido Principal de Empresa */}
      <main className="container flex-grow-1 py-4">
        <div className="row align-items-center g-5 mb-5">
          <div className="col-lg-6 col-12">
            <span className="badge bg-primary-subtle text-primary fw-bold px-3 py-1.5 rounded-pill text-uppercase mb-3">
              Trayectoria & Fabricación Directa
            </span>
            <h2 className="display-6 fw-extrabold text-dark mb-3">
              {about.title}
            </h2>
            <h5 className="text-primary fw-semibold mb-3 lh-base">
              {about.tagline}
            </h5>
            <p className="text-secondary fs-6 mb-3 lh-lg">
              {about.paragraph1}
            </p>
            <p className="text-secondary fs-6 mb-4 lh-lg">
              {about.paragraph2}
            </p>

            <div className="d-flex flex-wrap gap-3">
              <a href="/tienda" className="btn btn-primary fw-bold rounded-pill px-4 py-2.5 shadow-sm d-flex align-items-center gap-2">
                <i className="bi bi-bag-check-fill"></i> Explorar Catálogo
              </a>
              <a href="/contacto" className="btn btn-outline-secondary fw-bold rounded-pill px-4 py-2.5 d-flex align-items-center gap-2">
                <i className="bi bi-envelope"></i> Contactar a Ventas
              </a>
            </div>
          </div>

          <div className="col-lg-6 col-12">
            <div className="position-relative">
              {about.image ? (
                <img
                  src={getImageCandidateUrls(about.image)[0] || about.image}
                  alt={about.title || 'Planta Industrial'}
                  className="img-fluid rounded-4 shadow-lg border"
                  style={{ maxHeight: '440px', width: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  className="rounded-4 border bg-light d-flex flex-column align-items-center justify-content-center text-muted"
                  style={{ height: '340px', width: '100%' }}
                >
                  <i className="bi bi-building fs-1 opacity-50 mb-2"></i>
                  <span className="small fw-semibold">{config?.logoText || 'Nuestra Empresa'}</span>
                </div>
              )}
              <div className="position-absolute bottom-0 start-0 m-4 p-3 bg-white bg-opacity-95 rounded-3 shadow border d-flex align-items-center gap-3">
                <i className="bi bi-patch-check-fill text-success fs-1"></i>
                <div>
                  <h6 className="fw-bold text-dark mb-0">Fabricación Directa</h6>
                  <small className="text-muted">Garantía de stock permanente y precio de fábrica</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Métricas y Estadísticas */}
        {about.stats && about.stats.length > 0 && (
          <div className="bg-light p-4 p-lg-5 rounded-4 border mb-5">
            <div className="row g-4 text-center justify-content-center">
              {about.stats.map((stat, i) => {
                const total = about.stats.length;
                let colClass = 'col-6 col-md-3';
                if (total === 1) colClass = 'col-12 col-md-6';
                else if (total === 2) colClass = 'col-6 col-md-6';
                else if (total === 3) colClass = 'col-12 col-sm-6 col-md-4';
                else if (total === 4) colClass = 'col-6 col-md-3';
                else if (total === 5) colClass = 'col-6 col-md-4 col-lg';
                else if (total === 6) colClass = 'col-6 col-md-4 col-lg-2';
                else colClass = 'col-6 col-md-4 col-lg-3';

                return (
                  <div key={i} className={colClass}>
                    <div className="bg-white p-3 p-lg-4 rounded-3 shadow-xs border h-100 d-flex flex-column justify-content-center">
                      <h2 className="fw-black text-primary display-5 mb-1">{stat.number}</h2>
                      <span className="text-muted fw-bold small text-uppercase">{stat.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pilares Institucionales */}
        <div className="row g-4 mb-5">
          <div className="col-md-4">
            <div className="p-4 bg-white border rounded-4 shadow-xs h-100">
              <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '42px', height: '42px', fontSize: '1.2rem' }}>
                <i className="bi bi-boxes"></i>
              </div>
              <h5 className="fw-bold text-dark mb-2 fs-6">Variedad y Disponibilidad</h5>
              <p className="text-muted small mb-0 lh-base">
                Más de 1.000 artículos en stock permanente: cartón corrugado, cajas standard y a medida, bolsas plásticas, descartables y papel comercial.
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-4 bg-white border rounded-4 shadow-xs h-100">
              <div className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '42px', height: '42px', fontSize: '1.2rem' }}>
                <i className="bi bi-truck"></i>
              </div>
              <h5 className="fw-bold text-dark mb-2 fs-6">Logística Nacional</h5>
              <p className="text-muted small mb-0 lh-base">
                Despachos rápidos dentro de las 24 horas y coordinación con los principales transportes y expresos del país para llegar a cualquier provincia.
              </p>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-4 bg-white border rounded-4 shadow-xs h-100">
              <div className="bg-warning-subtle text-warning-emphasis rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '42px', height: '42px', fontSize: '1.2rem' }}>
                <i className="bi bi-tags-fill text-dark"></i>
              </div>
              <h5 className="fw-bold text-dark mb-2 fs-6">Venta Mayorista & Minorista</h5>
              <p className="text-muted small mb-0 lh-base">
                Escalas de precios por volumen y listas diferenciales para comercios, empresas e industrias con asesoramiento personalizado.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Unificado */}
      <UnifiedFooter storeConfig={storeConfig} landingConfig={config} />

      {/* Modales Compartidos */}
      <EcommerceCartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={(key, qty) => {
          if (qty <= 0) setCartItems(prev => prev.filter(i => i.cartKey !== key));
          else setCartItems(prev => prev.map(i => i.cartKey === key ? { ...i, quantity: qty } : i));
        }}
        onRemoveItem={(key) => setCartItems(prev => prev.filter(i => i.cartKey !== key))}
        onClearCart={() => setCartItems([])}
        onCheckout={() => {
          setIsCartOpen(false);
          setIsCheckoutOpen(true);
        }}
        canSeePrices={canSeePrices}
        onLoginClick={() => {
          setIsCartOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
      />

      <EcommerceCheckoutModal
        show={isCheckoutOpen}
        onHide={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        canSeePrices={canSeePrices}
        currentUser={activeCustomer}
        whatsappSalesNumber={config?.contactSection?.whatsapp || '5491142476622'}
        companyName={storeConfig?.footer?.companyName || 'HIPERPEL S.A.'}
        storeConfig={storeConfig}
        companyId={storeConfig?.company_id || 1}
        onRequireAuth={() => {
          setIsCheckoutOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
        onOrderCompleted={() => setCartItems([])}
      />

      <EcommerceAuthModal
        show={isAuthModalOpen}
        onHide={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onSuccess={(customer) => {
          setEcomCustomer(customer);
          if (setContextCustomer) {
            setContextCustomer(customer);
          } else {
            localStorage.setItem('sigecom_ecom_customer', JSON.stringify(customer));
            localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());
          }
          setIsAuthModalOpen(false);
        }}
        companyId={storeConfig?.company_id || 1}
      />

      {config?.whatsappFloating?.enabled !== false && (
        <EcommerceWhatsAppFloating
          phoneNumber={config?.whatsappFloating?.phone || config?.contactSection?.whatsapp || '5491142476622'}
          defaultMessage={config?.whatsappFloating?.message || `¡Hola! Me comunico desde la web de ${config?.logoText || 'la empresa'} para hacer una consulta institucional.`}
          showPill={config?.whatsappFloating?.showPill !== false}
          pillText={config?.whatsappFloating?.pillText || '¿Dudas? ¡Escribinos!'}
        />
      )}

    </div>
  );
};

export default EmpresaPage;
